import { useCallback, useState } from "react";
import { toast } from "sonner";

import { loadFromLocal, saveToLocal } from "@/utils/storage";
import type { PresetMap } from "./types";

/**
 * Gerencia os presets salvos pelo usuário, persistidos em localStorage por `storageKey`.
 * Quando `storageKey` é ausente, opera em memória e nenhuma persistência ocorre.
 */
export function useFilterPresets(storageKey: string | undefined) {
  const persistKey = storageKey ? `filterbar-presets:${storageKey}` : null;
  const enabled = !!persistKey;

  const [presets, setPresets] = useState<PresetMap>(() =>
    persistKey ? loadFromLocal(persistKey, {}) : {},
  );

  const persist = useCallback(
    (next: PresetMap) => {
      setPresets(next);
      if (persistKey) saveToLocal(persistKey, next);
    },
    [persistKey],
  );

  /** Salva os critérios atuais (sem a busca textual) sob `name`. */
  const save = useCallback(
    (name: string, criteria: Record<string, any> | null | undefined) => {
      const trimmed = name.trim();
      if (!trimmed) return false;

      const rest = { ...((criteria || {}) as Record<string, any>) };
      delete rest.search;

      if (Object.keys(rest).length === 0) {
        toast.error("Nenhum filtro ativo para salvar.");
        return false;
      }

      persist({ ...presets, [trimmed]: rest });
      toast.success(`Preset "${trimmed}" salvo!`);
      return true;
    },
    [presets, persist],
  );

  const remove = useCallback(
    (name: string) => {
      const next = { ...presets };
      delete next[name];
      persist(next);
    },
    [presets, persist],
  );

  return { enabled, presets, save, remove };
}
